// The agency's EHR tags, picked as chips on a compact approval card.
import { User, X } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

export type EhrNoteOptionsQuery =
    | Readonly<{ client_id: string }>
    | Readonly<{ caregiver_id: string }>
    | Readonly<{ shift_id: string }>;

export const CARD_CONTROL_CLASS =
    'text-v3-chat-meta h-7 rounded-[8px] px-2 font-normal shadow-none';
export const CARD_ADD_CLASS = `${CARD_CONTROL_CLASS} text-muted-foreground border-dashed`;

export function useEhrNoteOptions(
    organizationId: string,
    query: EhrNoteOptionsQuery,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/ehr-note-options',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: query,
        reason: 'v3-ehr-note-options',
        select: (response) => response,
    });
}

export function CardChip({
    label,
    person = false,
    disabled,
    onRemove,
}: Readonly<{
    label: string;
    person?: boolean;
    disabled: boolean;
    onRemove: () => void;
}>): ReactNode {
    return (
        <span className="bg-muted text-v3-chat-meta inline-flex h-7 max-w-full items-center gap-1 rounded-[8px] pr-0.5 pl-2">
            {person && (
                <User
                    aria-hidden={true}
                    className="text-muted-foreground size-3 shrink-0"
                />
            )}
            <span className="truncate">{label}</span>
            <button
                type="button"
                aria-label={`Remove ${label}`}
                disabled={disabled}
                onClick={onRemove}
                className="text-muted-foreground hover:text-foreground focus-visible:ring-ring rounded-[6px] p-0.5 focus-visible:ring-2 disabled:opacity-40"
            >
                <X aria-hidden={true} className="size-3" />
            </button>
        </span>
    );
}

export function EhrTagChips({
    options,
    value,
    disabled,
    onChange,
}: Readonly<{
    options: ReturnType<typeof useEhrNoteOptions>;
    value: ReadonlyArray<string>;
    disabled: boolean;
    onChange: (tagIds: ReadonlyArray<string>) => void;
}>): ReactNode {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const categories = options.data?.categories ?? [];
    const names = new Map(categories.map((tag) => [tag.id, tag.name]));
    return (
        <Fragment>
            {value.map((tagId) => (
                <CardChip
                    key={tagId}
                    label={names.get(tagId) ?? tagId}
                    disabled={disabled}
                    onRemove={() =>
                        onChange(value.filter((chosen) => chosen !== tagId))
                    }
                />
            ))}
            <Combobox
                open={open}
                onOpenChange={setOpen}
                placeholder={
                    options.error !== undefined
                        ? 'Tags unavailable'
                        : options.data === undefined
                          ? 'Loading tags…'
                          : '+ Tag'
                }
                disabled={disabled || options.data === undefined}
                searchValue={search}
                onSearchValueChange={setSearch}
                searchPlaceholder="Search tags…"
                fullWidth={false}
                showCaret={false}
                matchTriggerWidth={false}
                triggerClassName={CARD_ADD_CLASS}
                contentClassName="w-72"
                commandListProps={{ className: 'max-h-64' }}
            >
                <CommandEmpty className="text-muted-foreground text-v3-chat-meta py-6 text-center">
                    No tags found
                </CommandEmpty>
                {categories
                    .filter((tag) => !value.includes(tag.id))
                    .map((tag) => (
                        <CommandItem
                            key={tag.id}
                            value={tag.name}
                            onSelect={() => {
                                onChange([...value, tag.id]);
                                setOpen(false);
                            }}
                            className="text-v3-chat py-2 text-left"
                        >
                            {tag.name}
                        </CommandItem>
                    ))}
            </Combobox>
        </Fragment>
    );
}
